/**
 * One octave, with the chord's pitch classes lit.
 *
 * Correction is the moment where the app is most likely to be wrong and the player is
 * least likely to want to read. A row of chord symbols asks them to parse notation; a
 * keyboard with four keys lit asks them to recognise a shape, which is something a pianist
 * does faster than they can read "Cm7♭5". That is the entire justification for it.
 *
 * Pitch classes, not notes — the recogniser has no register and this must not imply one.
 */

import type { PitchClass } from '@hc/shared';

/** Semitone offsets that are black keys, and where they sit between the white ones. */
const WHITE_KEYS: readonly PitchClass[] = [0, 2, 4, 5, 7, 9, 11];
const BLACK_KEYS: ReadonlyArray<{ pc: PitchClass; afterWhiteIndex: number }> = [
  { pc: 1, afterWhiteIndex: 0 },
  { pc: 3, afterWhiteIndex: 1 },
  { pc: 6, afterWhiteIndex: 3 },
  { pc: 8, afterWhiteIndex: 4 },
  { pc: 10, afterWhiteIndex: 5 },
];

export interface MiniKeyboardProps {
  pitchClasses: readonly PitchClass[];
  /** Drawn with a stronger accent — the sounding bass. */
  bassPc?: PitchClass;
  label?: string;
}

export function MiniKeyboard({
  pitchClasses,
  bassPc,
  label,
}: MiniKeyboardProps): React.JSX.Element {
  const lit = new Set(pitchClasses);
  return (
    <span
      className="hc-keys"
      data-testid="hc-mini-keyboard"
      data-pitch-classes={[...pitchClasses].sort((a, b) => a - b).join(',')}
      role="img"
      aria-label={label ?? `${pitchClasses.length} notes`}
    >
      {WHITE_KEYS.map((pc) => (
        <span
          key={`w${pc}`}
          className={`hc-keys__white${lit.has(pc) ? ' is-lit' : ''}${bassPc === pc ? ' is-bass' : ''}`}
        />
      ))}
      {BLACK_KEYS.map(({ pc, afterWhiteIndex }) => (
        <span
          key={`b${pc}`}
          className={`hc-keys__black${lit.has(pc) ? ' is-lit' : ''}${bassPc === pc ? ' is-bass' : ''}`}
          // Centred on the boundary between two white keys; the offset is half the black
          // key's width, which `--hc-key-black-width` also sets in the stylesheet.
          style={{
            left: `calc(${((afterWhiteIndex + 1) / 7) * 100}% - (var(--hc-key-black-width) / 2))`,
          }}
        />
      ))}
    </span>
  );
}
